import { useCallback, useEffect, useMemo, useRef } from 'react';

import isEmpty from 'lodash/isEmpty';

import { fg } from '@atlaskit/platform-feature-flags';
import { useBFFDataProvider } from '@atlassian/navigation-bff-data-provider';

import { RequestStatus } from '../../constants';
import type {
	fnGetFilteredProductLinks,
	GlobalShortcutsItem,
	ProductLink,
	ShortcutsConfig,
} from '../../types';
import { productLinksWorkspaceCloudIdFilter } from '../product-link-filters';

import type { InferShortcutsOutput, UseGlobalShortcutDataInputBase } from './types';

/**
 * @param input.shortcutsConfig - Configuration for global shortcuts. Accepts either a `ShortcutsConfig` record or a `GlobalShortcutsItem[]` array; the return type matches the input shape.
 * @returns Shortcuts config with URLs hydrated from the BFF data provider.
 */
export const useGlobalShortcutsDataBFF = <
	T extends ShortcutsConfig | GlobalShortcutsItem[] | undefined = undefined,
>({
	shortcutsConfig,
	cloudId,
	product,
	onUpdateGetFilteredProductLinks,
	isShortcutsStatic,
	onError,
}: UseGlobalShortcutDataInputBase & { shortcutsConfig: T }): InferShortcutsOutput<T> => {
	const onUpdateGetFilteredProductLinksRef = useRef(onUpdateGetFilteredProductLinks);
	onUpdateGetFilteredProductLinksRef.current = onUpdateGetFilteredProductLinks;
	const { buildURLAndFetchData, status, productLinks, error } = useBFFDataProvider({
		request: {},
		cloudId,
		forceStatic: isShortcutsStatic,
		product,
	});

	const productLinksRef = useRef(productLinks);
	productLinksRef.current = productLinks;
	const statusRef = useRef(status);
	statusRef.current = status;

	const lastCompleteProductLinksRef = useRef<Record<string, ProductLink[]>>({});
	if (status === RequestStatus.COMPLETE) {
		lastCompleteProductLinksRef.current = { ...productLinks };
	}

	const getBffProductLinksForKey = useCallback<fnGetFilteredProductLinks>(
		(apsKey: string) => ({
			links:
				(fg('navx-gas-getlink-unstable-fix')
					? lastCompleteProductLinksRef.current
					: productLinksRef.current)[apsKey] ?? [],
			status: fg('navx-gas-getlink-unstable-fix') ? RequestStatus.COMPLETE : statusRef.current,
		}),
		[],
	);

	useEffect(() => {
		if (!onUpdateGetFilteredProductLinksRef.current) {
			return;
		}
		if (status !== RequestStatus.COMPLETE && fg('navx-gas-getlink-unstable-fix')) {
			return;
		}
		onUpdateGetFilteredProductLinksRef.current(getBffProductLinksForKey);
	}, [getBffProductLinksForKey, status, productLinks]);

	// fetch the data from the bff if required
	useEffect(() => {
		if (isEmpty(shortcutsConfig) || isShortcutsStatic || !cloudId) {
			return;
		}
		buildURLAndFetchData();
	}, [buildURLAndFetchData, cloudId, shortcutsConfig, isShortcutsStatic]);

	useEffect(() => {
		if ((status as RequestStatus | null) === RequestStatus.ERROR && error) {
			onError?.(error);
		}
	}, [status, error, onError]);

	// associate shortcuts with the returned bff data
	const hydratedAppShortcuts = useMemo(() => {
		if (isEmpty(shortcutsConfig)) {
			return shortcutsConfig;
		}

		const config = shortcutsConfig as ShortcutsConfig | GlobalShortcutsItem[];

		if (
			(status as RequestStatus | null) === RequestStatus.COMPLETE &&
			Object.keys(productLinks).length
		) {
			const isArray = Array.isArray(config);

			if (isArray) {
				return config.reduce<GlobalShortcutsItem[]>((acc, item) => {
					// if config does not need a data reference, just return the value
					if (item.type !== 'link' || !item.apsKey) {
						acc.push(item);
						return acc;
					}

					// if config requires the bff to determine the data reference
					const productLink = productLinks[item.apsKey];
					if (productLink) {
						const filteredProductLink = cloudId
							? productLink.filter(productLinksWorkspaceCloudIdFilter(cloudId))[0]
							: productLink[0];
						acc.push({
							...item,
							url: filteredProductLink.url,
						});
					}
					return acc;
				}, []);
			}

			const entries = Object.entries(config);
			return entries.reduce<ShortcutsConfig>((acc, [key, value]) => {
				// if config does not need a data reference, just return the value
				if (value.type !== 'link' || !value.apsKey) {
					acc[key] = value;
					return acc;
				}

				// if config requires the bff to determine the data reference
				const productLink = productLinks[value.apsKey];
				if (productLink) {
					const filteredProductLink = cloudId
						? productLink.filter(productLinksWorkspaceCloudIdFilter(cloudId))[0]
						: productLink[0];
					acc[key] = {
						...value,
						url: filteredProductLink.url,
					};
					return acc;
				}
				return acc;
			}, {});
		}

		return shortcutsConfig;
	}, [productLinks, status, shortcutsConfig, cloudId]);

	return hydratedAppShortcuts as InferShortcutsOutput<T>;
};
